import { useEffect, useState } from 'react';
import { NavigateOptions, useSearchParams } from 'react-router-dom';
import { useDeepCompareEffect } from 'use-deep-compare';

import type { INavigateFunction } from '~/components/utils/Navigation';
import { getCurrentLocation, useNavigate } from '~/components/utils/Navigation';
import { updateQueryStringValueWithoutNavigation } from '~/helpers/updateQueryStringValueWithoutNavigation';

const PAGINATION_PAGE_VAR_NAME = 'page';
const PAGINATION_PAGE_SIZE_VAR_NAME = 'limit';

export type IUsePaginationProps<TExtraParams> = {
  extraParams?: TExtraParams;
};

export type IUsePaginationResult = {
  start: number;
  limit: number;
  page: number;
  updateLimit: (limit: number) => void;
  updatePage: (page: number) => void;
};

const PAGINATION_PAGE_DEFAULT_VALUE = 1;
const PAGINATION_PAGE_SIZE_DEFAULT_VALUE = 10;

function updateSearchParamsAndNavigate(
  searchParamsToUpdate: {
    [PAGINATION_PAGE_VAR_NAME]?: string;
    [PAGINATION_PAGE_SIZE_VAR_NAME]?: string;
  },
  navigate: INavigateFunction,
  options?: NavigateOptions,
): void {
  const currentUrl = new URL(getCurrentLocation());
  const searchParams = currentUrl.searchParams;

  for (const [key, value] of Object.entries(searchParamsToUpdate)) {
    searchParams.set(key, value);
  }

  navigate(`${currentUrl.pathname}?${searchParams.toString()}`, options);
}

function sanityCheck(
  searchParams: URLSearchParams,
  navigate: INavigateFunction,
): void {
  const pageCandidate = Number(
    searchParams.get(PAGINATION_PAGE_VAR_NAME)
      ? searchParams.get(PAGINATION_PAGE_VAR_NAME)
      : PAGINATION_PAGE_DEFAULT_VALUE,
  );
  const limitCandidate = Number(
    searchParams.get(PAGINATION_PAGE_SIZE_VAR_NAME)
      ? searchParams.get(PAGINATION_PAGE_SIZE_VAR_NAME)
      : PAGINATION_PAGE_SIZE_DEFAULT_VALUE,
  );

  // In case the URL is missing the search parameters or is not an integer
  // -> Add the search paremeters and navigate to the URL
  if (
    !searchParams.has(PAGINATION_PAGE_VAR_NAME) ||
    !searchParams.has(PAGINATION_PAGE_SIZE_VAR_NAME) ||
    !Number.isInteger(pageCandidate) ||
    !Number.isInteger(limitCandidate)
  ) {
    updateSearchParamsAndNavigate(
      {
        [PAGINATION_PAGE_VAR_NAME]: String(PAGINATION_PAGE_DEFAULT_VALUE),
        [PAGINATION_PAGE_SIZE_VAR_NAME]: String(
          PAGINATION_PAGE_SIZE_DEFAULT_VALUE,
        ),
      },
      navigate,
      { replace: true },
    );
  }

  const page = pageCandidate;
  const limit = limitCandidate;

  // In case the page index is inferior to the default value
  // -> Set page index to the default value and navigate to the URL
  if (page < PAGINATION_PAGE_DEFAULT_VALUE) {
    updateSearchParamsAndNavigate(
      {
        [PAGINATION_PAGE_VAR_NAME]: String(PAGINATION_PAGE_DEFAULT_VALUE),
      },
      navigate,
      { replace: true },
    );
  }

  // In case the limit is inferior to the default value
  // -> Set limit to the default value and navigate to the URL
  if (limit < PAGINATION_PAGE_SIZE_DEFAULT_VALUE) {
    updateSearchParamsAndNavigate(
      {
        [PAGINATION_PAGE_SIZE_VAR_NAME]: String(
          PAGINATION_PAGE_SIZE_DEFAULT_VALUE,
        ),
      },
      navigate,
      { replace: true },
    );
  }
}

export const usePagination = <TExtraParams extends Record<string, unknown>>(
  props?: IUsePaginationProps<TExtraParams>,
): IUsePaginationResult => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    setPage(Number(searchParams.get(PAGINATION_PAGE_VAR_NAME)));
    setLimit(Number(searchParams.get(PAGINATION_PAGE_SIZE_VAR_NAME)));

    sanityCheck(searchParams, navigate);
  }, [navigate, searchParams]);

  useDeepCompareEffect(() => {
    if (!props?.extraParams) {
      return;
    }

    Object.entries(props?.extraParams).forEach(([key, value]) =>
      updateQueryStringValueWithoutNavigation(
        key,
        value &&
          (typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean')
          ? String(value)
          : undefined,
      ),
    );
  }, [props?.extraParams]);

  /** 'page', is one-based indexed, matches the index of the page to display. */
  const [page, setPage] = useState(
    Number(searchParams.get(PAGINATION_PAGE_VAR_NAME)),
  );

  /**
   * 'limit', used in the request to the server as 'limit', matches the number
   * of articles returned by the server.
   */
  const [limit, setLimit] = useState(
    Number(searchParams.get(PAGINATION_PAGE_SIZE_VAR_NAME)),
  );

  /**
   * 'start', used in the request to the server, is zero-based indexed, matches
   * the index of the first article to return. Let consider a page size of 10.
   * In the case of the first page, start = 0 In the case of the second page,
   * start = 10.
   */
  const [start, setStart] = useState((page - 1) * limit);

  useEffect(() => {
    setStart((page - 1) * limit);
  }, [page, limit]);

  function updatePage(pageArg: number): void {
    setPage(Math.max(pageArg, PAGINATION_PAGE_DEFAULT_VALUE));

    updateQueryStringValueWithoutNavigation(
      PAGINATION_PAGE_VAR_NAME,
      String(Math.max(pageArg, PAGINATION_PAGE_DEFAULT_VALUE)),
    );
  }

  function updateLimit(limit: number): void {
    setLimit(limit);

    updateQueryStringValueWithoutNavigation(
      PAGINATION_PAGE_SIZE_VAR_NAME,
      String(limit),
    );
  }

  return {
    start,
    limit,
    page,
    updateLimit,
    updatePage,
  };
};
